import * as React from "react";

import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import VolumeMuteIcon from "@mui/icons-material/VolumeMute";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ListItem from "@mui/material/ListItem";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";

import { logFetchError, pairedFetchApi } from "../../../util/fetchApi";
import { useStatus } from "../../hooks/status";
import { useTrainerFeatures } from "../../hooks/useTrainerFeatures";

const STORAGE_KEY = "volley-volume-pref";

function loadStoredPct(): number {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored !== null) {
            const val = Number(stored);
            if (!Number.isNaN(val) && val >= 0 && val <= 100) {
                return val;
            }
        }
    } catch {
        // localStorage unavailable
    }
    return 100;
}

function saveStoredPct(pct: number): void {
    try {
        localStorage.setItem(STORAGE_KEY, String(pct));
    } catch {
        // localStorage unavailable
    }
}

function pctToVolume(pct: number, maxVolume: number): number {
    return Math.round((pct / 100) * maxVolume);
}

export default function VolumeListItem(): React.JSX.Element | null {
    const { status } = useStatus();
    const clientId = status?.clientId;
    const features = useTrainerFeatures();
    const supported = features.includes("remoteConfig");

    const [maxVolume, setMaxVolume] = React.useState(64);
    const [pct, setPct] = React.useState(loadStoredPct);
    const [loaded, setLoaded] = React.useState(false);

    // Fetch the admin-configured max volume
    React.useEffect(() => {
        if (!clientId || !supported) return;

        pairedFetchApi<{ maxVolume: number }>(clientId, "/api/volume")
            .then((data) => {
                setMaxVolume(data.maxVolume);
                setLoaded(true);
            })
            .catch((e) => {
                logFetchError(e, "Error fetching volume config");
                setLoaded(true);
            });
    }, [clientId, supported]);

    // Apply stored preference on initial load
    const appliedRef = React.useRef(false);
    React.useEffect(() => {
        if (!loaded || !clientId || !supported || appliedRef.current) return;
        appliedRef.current = true;

        const storedPct = loadStoredPct();
        if (storedPct < 100) {
            const volume = pctToVolume(storedPct, maxVolume);
            pairedFetchApi(clientId, "/api/volume", "POST", { volume }).catch(
                (e) => logFetchError(e, "Error applying stored volume"),
            );
        }
    }, [loaded, clientId, maxVolume, supported]);

    // Debounced send to trainer
    const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const sendVolume = React.useCallback(
        (newPct: number) => {
            if (!clientId) return;
            if (timerRef.current) clearTimeout(timerRef.current);

            timerRef.current = setTimeout(() => {
                const volume = pctToVolume(newPct, maxVolume);
                pairedFetchApi(clientId, "/api/volume", "POST", {
                    volume,
                }).catch((e) => logFetchError(e, "Error setting volume"));
            }, 150);
        },
        [clientId, maxVolume],
    );

    const handleChange = React.useCallback(
        (_event: Event, value: number | number[]) => {
            const newPct = value as number;
            setPct(newPct);
            saveStoredPct(newPct);
            sendVolume(newPct);
        },
        [sendVolume],
    );

    if (!supported) return null;

    const icon =
        pct === 0 ? (
            <VolumeMuteIcon color="action" />
        ) : pct < 50 ? (
            <VolumeDownIcon color="action" />
        ) : (
            <VolumeUpIcon color="action" />
        );

    return (
        <ListItem
            sx={{
                py: 1.5,
                px: 2,
                backgroundColor: (theme) => theme.palette.common.white,
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                {icon}
                <Slider
                    value={pct}
                    onChange={handleChange}
                    min={0}
                    max={100}
                    step={5}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}%`}
                    size="small"
                    sx={{ flex: 1 }}
                />
            </Stack>
        </ListItem>
    );
}
